import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { 
  AlertTriangle, 
  Filter, 
  Search, 
  CheckCircle, 
  Plus, 
  Calendar,
  MapPin,
  Clock,
  Eye,
  ChevronDown
} from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'

import { 
  useGetAlertsQuery, 
  useAcknowledgeAlertMutation, 
  useAcknowledgeMultipleAlertsMutation,
} from '@/store/api'
import { Alert, AlertFilters } from '@/types'
import { formatDistanceToNow } from '@/lib/utils'
import AlertDetailModal from '@/components/alerts/AlertDetailModal'
import CreateWorkOrderModal from '@/components/alerts/CreateWorkOrderModal'
import AlertFiltersPanel from '@/components/alerts/AlertFiltersPanel'

export default function AlertsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  
  // State
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedAlerts, setSelectedAlerts] = useState<string[]>([])
  const [showFilters, setShowFilters] = useState(false)
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null)
  const [showWorkOrderModal, setShowWorkOrderModal] = useState(false)
  const [workOrderAlert, setWorkOrderAlert] = useState<Alert | null>(null)
  
  // Filters from URL params
  const filters: AlertFilters = useMemo(() => ({
    priority: searchParams.getAll('priority') as Alert['priority'][],
    status: searchParams.getAll('status') as Alert['status'][],
    equipmentType: searchParams.getAll('equipmentType') as any[],
    propertyId: searchParams.get('propertyId') || undefined
  }), [searchParams])

  // Pagination
  const [page, setPage] = useState(1)
  const [limit] = useState(20)

  // API calls
  const { 
    data: alertsData, 
    isLoading, 
    error,
    refetch 
  } = useGetAlertsQuery({
    page,
    limit,
    search: searchTerm,
    sortBy: 'created_at',
    sortOrder: 'desc',
    priority: filters.priority?.filter(p => p !== undefined) as string[],
    status: filters.status?.filter(s => s !== undefined) as string[],
    equipmentType: filters.equipmentType?.filter(e => e !== undefined) as string[],
    propertyId: filters.propertyId
  })

  const [acknowledgeAlert] = useAcknowledgeAlertMutation()
  const [acknowledgeMultiple] = useAcknowledgeMultipleAlertsMutation()

  const alerts = alertsData?.data || []
  const pagination = alertsData?.pagination

  // Handle URL params on mount
  useEffect(() => {
    const initialSearch = searchParams.get('search')
    if (initialSearch) {
      setSearchTerm(initialSearch)
    }
  }, [searchParams])

  // Update URL when filters change
  const updateFilters = (newFilters: Partial<AlertFilters>) => {
    const params = new URLSearchParams()
    
    if (searchTerm) params.set('search', searchTerm)
    
    Object.entries({ ...filters, ...newFilters }).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(v => v && params.append(key, String(v)))
      } else if (value) {
        params.set(key, String(value))
      }
    })
    
    setSearchParams(params)
  }

  // Handle search
  const handleSearch = (term: string) => {
    setSearchTerm(term)
    if (term) {
      searchParams.set('search', term)
    } else {
      searchParams.delete('search')
    }
    setSearchParams(searchParams)
    setPage(1)
  }

  // Handle alert selection
  const handleSelectAlert = (alertId: string) => {
    setSelectedAlerts(prev => 
      prev.includes(alertId) 
        ? prev.filter(id => id !== alertId)
        : [...prev, alertId]
    )
  }

  const handleSelectAll = () => {
    if (selectedAlerts.length === alerts.length) {
      setSelectedAlerts([])
    } else {
      setSelectedAlerts(alerts.map(alert => alert.id))
    }
  }

  // Handle acknowledge
  const handleAcknowledge = async (alertId: string) => {
    try {
      await acknowledgeAlert(alertId).unwrap()
      refetch()
    } catch (error) {
      if (import.meta.env.DEV) {
        console.debug('Failed to acknowledge alert:', error)
      }
      // Error handling can be added here (e.g., toast notification)
    }
  }

  const handleAcknowledgeMultiple = async () => {
    try {
      await acknowledgeMultiple(selectedAlerts).unwrap()
      setSelectedAlerts([])
      refetch()
    } catch (error) {
      if (import.meta.env.DEV) {
        console.debug('Failed to acknowledge alerts:', error)
      }
      // Error handling can be added here (e.g., toast notification)
    }
  }

  // Handle work order creation
  const handleCreateWorkOrder = (alert: Alert) => {
    setWorkOrderAlert(alert)
    setShowWorkOrderModal(true)
  }

  const handleWorkOrderCreated = () => {
    setShowWorkOrderModal(false)
    setWorkOrderAlert(null)
    refetch()
  }

  // Severity stats
  const severityStats = useMemo(() => {
    return alerts.reduce((acc, alert) => {
      const severity = alert.severity || alert.priority || 'unknown'
      acc[severity] = (acc[severity] || 0) + 1
      return acc
    }, {} as Record<string, number>)
  }, [alerts])

  const getSeverityVariant = (severity: string): 'critical' | 'warning' | 'info' | 'default' => {
    switch (severity) {
      case 'critical': return 'critical'
      case 'high': return 'critical'
      case 'medium': return 'warning'
      case 'low': return 'info'
      default: return 'default'
    }
  }

  const getStatusVariant = (status: Alert['status']): 'success' | 'warning' | 'info' | 'default' => {
    switch (status) {
      case 'resolved': return 'success'
      case 'acknowledged': return 'warning'
      case 'work-order-created': return 'info'
      case 'new': return 'default'
      default: return 'default'
    }
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Predictive Alerts</h1>
          <p className="text-lg text-gray-600">
            Monitor and respond to equipment maintenance alerts
          </p>
        </div>
        
        {/* Priority Overview */}
        <div className="flex flex-wrap gap-2">
          <Badge variant="critical" className="px-3 py-1">
            Critical: {severityStats.critical || 0}
          </Badge>
          <Badge variant="critical" className="px-3 py-1">
            High: {severityStats.high || 0}
          </Badge>
          <Badge variant="warning" className="px-3 py-1">
            Medium: {severityStats.medium || 0}
          </Badge>
          <Badge variant="info" className="px-3 py-1">
            Low: {severityStats.low || 0}
          </Badge>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search alerts by title, equipment, or location..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            {/* Filter Toggle */}
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2"
            >
              <Filter className="w-4 h-4" />
              Filters
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </Button>

            {/* Bulk Actions */}
            {selectedAlerts.length > 0 && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleAcknowledgeMultiple}
                  className="flex items-center gap-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  Acknowledge ({selectedAlerts.length})
                </Button>
              </div>
            )}
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-6 pt-6 border-t">
              <AlertFiltersPanel
                filters={filters}
                onFiltersChange={updateFilters}
                onClearFilters={() => updateFilters({
                  priority: [],
                  status: [],
                  equipmentType: [],
                  propertyId: undefined
                })}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Alerts List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Alerts ({pagination?.total || 0})
            </CardTitle>
            
            {alerts.length > 0 && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedAlerts.length === alerts.length}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-600">Select All</span>
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg">
                    <div className="w-4 h-4 bg-gray-200 rounded"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                    </div>
                    <div className="space-y-1">
                      <div className="w-16 h-6 bg-gray-200 rounded"></div>
                      <div className="w-20 h-6 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load alerts</h3>
              <p className="text-gray-600 mb-4">There was an error loading the alerts data.</p>
              <Button onClick={() => refetch()}>Try Again</Button>
            </div>
          ) : alerts.length === 0 ? (
            <div className="text-center py-8">
              <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No alerts found</h3>
              <p className="text-gray-600">
                {searchTerm || Object.values(filters).some(f => f && (Array.isArray(f) ? f.length > 0 : true))
                  ? 'Try adjusting your search criteria or filters.'
                  : 'All systems are running smoothly!'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {alerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`flex items-start space-x-4 p-4 border rounded-lg transition-colors hover:bg-gray-50 ${
                    selectedAlerts.includes(alert.id) ? 'border-trust-blue bg-blue-50' : 'border-gray-200'
                  }`}
                >
                  {/* Selection Checkbox */}
                  <input
                    type="checkbox"
                    checked={selectedAlerts.includes(alert.id)}
                    onChange={() => handleSelectAlert(alert.id)}
                    className="mt-1 rounded border-gray-300"
                  />

                  {/* Priority Indicator */}
                  <div className={`w-1 h-16 rounded-full ${
                    alert.severity === 'critical' ? 'bg-red-500' :
                    alert.severity === 'high' ? 'bg-orange-500' :
                    alert.severity === 'medium' ? 'bg-yellow-500' :
                    'bg-blue-500'
                  }`}></div>

                  {/* Alert Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-gray-900 truncate">{alert.title}</h3>
                      <div className="flex items-center gap-2 ml-4">
                        <Badge variant={getSeverityVariant(alert.severity || alert.priority || 'unknown')}>
                          {((alert.severity || alert.priority || 'unknown')).charAt(0).toUpperCase() + (alert.severity || alert.priority || 'unknown').slice(1)}
                        </Badge>
                        <Badge variant={getStatusVariant(alert.status)}>
                          {alert.status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </Badge>
                      </div>
                    </div>

                    <p className="text-gray-600 text-sm mb-3">{alert.description}</p>

                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        <span>{alert.equipmentName || 'Unknown Equipment'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>{formatDistanceToNow(new Date(alert.createdAt))} ago</span>
                      </div>
                      {alert.prediction && (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          <span>Failure in {alert.prediction.timeToFailure}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedAlert(alert)}
                      className="flex items-center gap-1"
                    >
                      <Eye className="w-3 h-3" />
                      View
                    </Button>
                    
                    {alert.status === 'new' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAcknowledge(alert.id)}
                        className="flex items-center gap-1"
                      >
                        <CheckCircle className="w-3 h-3" />
                        Acknowledge
                      </Button>
                    )}
                    
                    {(alert.status === 'new' || alert.status === 'acknowledged') && (
                      <Button
                        size="sm"
                        onClick={() => handleCreateWorkOrder(alert)}
                        className="flex items-center gap-1"
                      >
                        <Plus className="w-3 h-3" />
                        Work Order
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-6 border-t">
              <div className="text-sm text-gray-600">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} alerts
              </div>
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                
                {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                  const pageNum = i + 1
                  return (
                    <Button
                      key={pageNum}
                      variant={page === pageNum ? "default" : "outline"}
                      size="sm"
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  )
                })}
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page === pagination.totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Alert Detail Modal */}
      {selectedAlert && (
        <AlertDetailModal
          alert={selectedAlert}
          isOpen={!!selectedAlert}
          onClose={() => setSelectedAlert(null)}
          onAcknowledge={handleAcknowledge}
          onCreateWorkOrder={handleCreateWorkOrder}
        />
      )}

      {/* Create Work Order Modal */}
      {showWorkOrderModal && workOrderAlert && (
        <CreateWorkOrderModal
          alert={workOrderAlert}
          isOpen={showWorkOrderModal}
          onClose={() => setShowWorkOrderModal(false)}
          onSuccess={handleWorkOrderCreated}
        />
      )}
    </div>
  )
}